'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { ProductStockStatus } from '@/fo/api/magento';
import {
  storeProduct,
  selectCountProductInWishlist,
  cartActions,
  wishlistActions,
  compareActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCountProductInCart,
  selectCompareProducts
} from '@/fo/store/product';
import { TAPBAR_MOBILE_ID } from '@/fo/ui/app/tapbar/mobile';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiModPortalBase } from '@/fo/ui/mod/portal/base';
import { UiProductCompareButton } from '@/fo/ui/product/compare/button';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './FeatureProductHeaderMobile.styles';

export interface FeatureProductHeaderMobileProps {
  product: MappedProduct;
  isAuthorized: boolean;
}

export function FeatureProductHeaderMobile({
  product: initialProduct,
  isAuthorized
}: FeatureProductHeaderMobileProps) {
  const slots = styles();
  const t = useTranslations();
  const router = useRouter();

  const isMobile = useMediaQuery('lg', 'max');

  // Используем продукт из store если есть, иначе из пропсов
  const [product, setProduct] = useState<MappedProduct>(
    initialProduct.configurableVariants?.find((item) => item.isSelected)
      ?.product || initialProduct
  );
  const [hasTapbarContainer, setHasTapbarContainer] = useState(false);

  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(product.sku)
  );
  const cartItemId = useSelector(storeProduct, selectCartItemId(product.sku));
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(product.sku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(product.sku)
  );

  const compareItemId = useSelector(
    storeProduct,
    selectCompareProducts(product.sku)
  );

  useEffect(() => {
    const checkContainer = () => {
      const container = document.getElementById(TAPBAR_MOBILE_ID);
      setHasTapbarContainer(!!container);
    };

    checkContainer();

    const observer = new MutationObserver(checkContainer);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const unsubscribeOnProductSelect =
      utilEventConfigurableProduct.onProductSelect((product) => {
        setProduct(product);
      });

    return () => {
      unsubscribeOnProductSelect();
    };
  });

  const isInWishlist = wishlistCount > 0;
  const isInCart = cartCount > 0;
  const isInCompare = compareItemId !== '';

  const onCartChange = async (quantity: number) => {
    if (!cartItemId) return;

    if (quantity === 0) {
      await cartActions.removeCartItem(product.sku, cartItemId, product);
    } else {
      await cartActions.updateCartItem(
        product.sku,
        cartItemId,
        quantity,
        product
      );
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          { sku: product.sku, wishlistItemId }
        ]);
      } else {
        wishlistActions.addProductToWishlist(product.sku, 1);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onCartClick = () => {
    if (product.stockStatus === ProductStockStatus.OutOfStock) {
      utilEventProductSubscribeModal.open(product.mindboxId);
      return;
    }

    if (isInCart) {
      router.push('/cart');
    } else {
      cartActions.addProductsToCart([
        {
          sku: product.sku,
          quantity: 1,
          product
        }
      ]);
    }
  };

  const onCompareClick = () => {
    if (isAuthorized) {
      if (isInCompare) {
        compareActions.removeProductFromCompare(product.id);
      } else {
        compareActions.addProductToCompare(product.id, {
          name: product.name,
          imageUrl: product.baseImage?.src ?? ''
        });
      }
    } else {
      utilEventAuthModal.open('compare-button');
    }
  };

  if (!isMobile || !hasTapbarContainer) return null;

  return (
    <UiModPortalBase
      getContainer={() => document.getElementById(TAPBAR_MOBILE_ID)}
    >
      <div className={slots.base()}>
        {cartCount > 0 && (
          <UiProductCounterInputBase
            onDebounceChange={onCartChange}
            className={slots.counter()}
            value={cartCount}
          />
        )}
        {!cartCount && (
          <UiKitButtonBase
            className={slots.addToCart()}
            onClick={onCartClick}
            color="secondary"
            disabled={product.stockStatus === 'OUT_OF_STOCK'}
          >
            {product.stockStatus === 'OUT_OF_STOCK' && t('Out of stock')}
            {product.stockStatus === 'IN_STOCK' &&
              cartCount > 0 &&
              t('In cart')}
            {product.stockStatus === 'IN_STOCK' &&
              cartCount === 0 &&
              t('To cart')}
          </UiKitButtonBase>
        )}
        <div className={slots.actions()}>
          <div className={slots.wishlist()}>
            <UiProductCompareButton
              className={slots.compare()}
              isActive={isInCompare}
              onClick={onCompareClick}
            />
          </div>
          <div className={slots.wishlist()}>
            <UiProductWishlistButton
              className={slots.wishlistIcon()}
              isActive={isInWishlist}
              onClick={onWishlistClick}
            />
          </div>
        </div>
      </div>
    </UiModPortalBase>
  );
}
